import {observer} from "mobx-react-lite";
import React, {useEffect, useMemo, useRef, useState} from "react";
import GameStore from "../../store/game";

const ALLOWED_DIRECTIONS = [
    {dr: 0, dc: 1}, // Горизонталь вправо
    {dr: 1, dc: 0}, // Вертикаль вниз
    {dr: 1, dc: 1}, // Диагональ вниз-вправо
    {dr: 1, dc: -1}, // Диагональ вниз-влево
    {dr: 0, dc: -1}, // Горизонталь влево
    {dr: -1, dc: 0}, // Вертикаль вверх
    {dr: -1, dc: -1}, // Диагональ вверх-влево
    {dr: -1, dc: 1}, // Диагональ вверх-вправо
];
const IOS_EDGE_GUARD_PX = 20;

const buildPath = (start, end) => {
    const rowDelta = end.row - start.row;
    const colDelta = end.col - start.col;

    if (rowDelta === 0 && colDelta === 0) {
        return [start];
    }

    const absRow = Math.abs(rowDelta);
    const absCol = Math.abs(colDelta);
    const rowStep = rowDelta === 0 ? 0 : rowDelta / absRow;
    const colStep = colDelta === 0 ? 0 : colDelta / absCol;

    const allowed = ALLOWED_DIRECTIONS.some((item) => item.dr === rowStep && item.dc === colStep);
    if (!allowed) {
        return [];
    }

    if (!(rowDelta === 0 || colDelta === 0 || absRow === absCol)) {
        return [];
    }

    const distance = Math.max(absRow, absCol);
    return Array.from({length: distance + 1}, (_, index) => ({
        row: start.row + rowStep * index,
        col: start.col + colStep * index,
    }));
};

// Игровое поле 7x7 с выбором слов по свайпу/drag.
export const GameBoard = observer(({
    onSelectionChange,
    onSelectionComplete,
    isDisabled,
    showTargetContours = false,
    hintRevealPath = [],
}) => {
    const [isSelecting, setIsSelecting] = useState(false);
    const startCellRef = useRef(null);

    // На iOS свайп от самого края экрана часто перехватывается браузером как системная навигация.
    const isEdgeGestureStart = (clientX) => {
        if (!Number.isFinite(clientX) || typeof window === "undefined") {
            return false;
        }

        return clientX <= IOS_EDGE_GUARD_PX || clientX >= window.innerWidth - IOS_EDGE_GUARD_PX;
    };

    const foundCellSet = useMemo(() => {
        const set = new Set();
        (GameStore.foundWordPaths || []).forEach((path) => {
            if (!Array.isArray(path)) {
                return;
            }
            path.forEach((item) => {
                const row = Number(item?.row);
                const col = Number(item?.col);
                if (Number.isFinite(row) && Number.isFinite(col)) {
                    set.add(`${row}-${col}`);
                }
            });
        });
        return set;
    }, [GameStore.foundWordPaths]);

    const selectedCellSet = useMemo(() => {
        const set = new Set();
        (GameStore.selectionCells || []).forEach((item) => {
            set.add(`${item.row}-${item.col}`);
        });
        return set;
    }, [GameStore.selectionCells]);

    const targetCellSet = useMemo(() => {
        if (!showTargetContours) {
            return new Set();
        }

        const set = new Set();
        (GameStore.targetWordPaths || []).forEach((path) => {
            if (!Array.isArray(path)) {
                return;
            }
            path.forEach((item) => {
                const row = Number(item?.row);
                const col = Number(item?.col);
                if (Number.isFinite(row) && Number.isFinite(col)) {
                    set.add(`${row}-${col}`);
                }
            });
        });
        return set;
    }, [showTargetContours, GameStore.targetWordPaths]);

    const hintCellSet = useMemo(() => {
        const set = new Set();
        if (!Array.isArray(hintRevealPath)) {
            return set;
        }

        hintRevealPath.forEach((item) => {
            const row = Number(item?.row);
            const col = Number(item?.col);
            if (Number.isFinite(row) && Number.isFinite(col)) {
                set.add(`${row}-${col}`);
            }
        });
        return set;
    }, [hintRevealPath]);

    const stopSelection = (shouldSubmit) => {
        if (!isSelecting) {
            return;
        }

        setIsSelecting(false);
        const finalPath = Array.isArray(GameStore.selectionCells) ? [...GameStore.selectionCells] : [];

        if (shouldSubmit && finalPath.length > 1 && typeof onSelectionComplete === "function") {
            onSelectionComplete(finalPath);
            return;
        }

        if (typeof onSelectionChange === "function") {
            onSelectionChange([]);
        }
    };

    const updateSelectionByPoint = (clientX, clientY) => {
        if (!isSelecting || !startCellRef.current || isDisabled) {
            return;
        }

        // На мобильных берём ячейку под пальцем через координаты указателя.
        const cellElement = document
            .elementFromPoint(clientX, clientY)
            ?.closest?.("[data-cell='true']");

        if (!cellElement) {
            return;
        }

        const row = Number(cellElement.dataset.row);
        const col = Number(cellElement.dataset.col);
        if (!Number.isFinite(row) || !Number.isFinite(col)) {
            return;
        }

        const path = buildPath(startCellRef.current, {row, col});
        if (path.length === 0) {
            onSelectionChange?.([]);
            return;
        }

        onSelectionChange?.(path);
    };

    useEffect(() => {
        const handlePointerUp = () => stopSelection(true);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerUp);

        return () => {
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerUp);
        };
    }, [isSelecting, onSelectionChange, onSelectionComplete]);

    useEffect(() => {
        const handlePointerMove = (event) => {
            if (event.pointerType === "touch" && event.cancelable && isSelecting) {
                event.preventDefault();
            }

            updateSelectionByPoint(event.clientX, event.clientY);
        };

        window.addEventListener("pointermove", handlePointerMove, {passive: false});
        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
        };
    }, [isSelecting, isDisabled, onSelectionChange]);

    useEffect(() => {
        const handleTouchMove = (event) => {
            if (!isSelecting || isDisabled) {
                return;
            }

            const touch = event.touches?.[0];
            if (!touch) {
                return;
            }

            if (event.cancelable) {
                event.preventDefault();
            }

            updateSelectionByPoint(touch.clientX, touch.clientY);
        };

        window.addEventListener("touchmove", handleTouchMove, {passive: false});
        return () => {
            window.removeEventListener("touchmove", handleTouchMove);
        };
    }, [isSelecting, isDisabled, onSelectionChange]);

    useEffect(() => {
        const handleTouchEnd = () => stopSelection(true);
        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", handleTouchEnd);

        return () => {
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [isSelecting, onSelectionChange, onSelectionComplete]);

    const handlePointerDown = (row, col, pointerType, clientX) => {
        if (isDisabled) {
            return;
        }

        if (pointerType === "touch" || isEdgeGestureStart(clientX)) {
            return;
        }

        const start = {row, col};
        startCellRef.current = start;
        setIsSelecting(true);
        onSelectionChange?.([start]);
    };

    const handlePointerEnter = (row, col) => {
        if (!isSelecting || !startCellRef.current || isDisabled) {
            return;
        }

        const path = buildPath(startCellRef.current, {row, col});
        if (path.length === 0) {
            onSelectionChange?.([]);
            return;
        }

        onSelectionChange?.(path);
    };

    if (!Array.isArray(GameStore.grid) || GameStore.grid.length === 0) {
        return (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">
                Поле загружается...
            </div>
        );
    }

    return (
        <div className="px-5 sm:px-4">
            <div
                className="grid grid-cols-7 gap-1.5 select-none touch-none overscroll-contain rounded-xl bg-gray-100/70 p-2 border border-gray-300/60 w-full max-w-[23rem] mx-auto"
                onTouchEnd={() => stopSelection(true)}
                onTouchCancel={() => stopSelection(true)}
            >
                {GameStore.grid.map((row, rowIndex) => row.map((cell, colIndex) => {
                    const key = `${rowIndex}-${colIndex}`;
                    const isFound = foundCellSet.has(key);
                    const isSelected = selectedCellSet.has(key);
                    const isTarget = targetCellSet.has(key);
                    const isHintRevealed = hintCellSet.has(key);
                    const foundWordClasses = "bg-gray-300 border-gray-400 !text-gray-900 font-semibold shadow-sm";
                    const activeSelectionClasses = "bg-brand-success border-brand-success/80 !text-white font-semibold shadow-sm";
                    const shouldShowFoundState = isFound && !isSelected;
                    const shouldShowHintBorder = isHintRevealed && !isSelected;
                    const hintBorderClasses = shouldShowHintBorder
                        ? "border-yellow-500 shadow-sm shadow-yellow-500/20"
                        : "";
                    const baseCellStateClasses = isSelected
                        ? "text-white"
                        : "bg-gray-200/80 border-gray-300 text-gray-700";
                    
                    const classes = [
                        "aspect-square rounded-lg border text-sm leading-none uppercase flex items-center justify-center transition-colors duration-150",
                        `${baseCellStateClasses} !p-0 !m-0 min-h-0 appearance-none`,
                        isTarget ? "ring-2 ring-yellow-500/45 ring-inset" : "",
                        hintBorderClasses,
                        shouldShowFoundState ? foundWordClasses : "",
                        isSelected ? activeSelectionClasses : "",
                    ].join(" ");

                    return (
                        <div
                            key={key}
                            role="button"
                            data-cell="true"
                            data-row={rowIndex}
                            data-col={colIndex}
                            tabIndex={isDisabled ? -1 : 0}
                            className={classes}
                            onPointerDown={(event) => handlePointerDown(rowIndex, colIndex, event.pointerType, event.clientX)}
                            onPointerEnter={() => handlePointerEnter(rowIndex, colIndex)}
                            onTouchStart={(event) => {
                                const touch = event.touches?.[0];
                                if (!touch || isEdgeGestureStart(touch.clientX)) {
                                    return;
                                }

                                if (event.cancelable) {
                                    event.preventDefault();
                                }

                                handlePointerDown(rowIndex, colIndex, "manual-touch", touch.clientX);
                            }}
                            onKeyDown={(event) => {
                                if (isDisabled) {
                                    return;
                                }

                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    handlePointerDown(rowIndex, colIndex, "keyboard");
                                }
                            }}
                        >
                            <span>{cell}</span>
                        </div>
                    );
                }))}
            </div>
        </div>
    );
});
